import type { QueryResult, TypedDocumentNode } from '@apollo/client';
import { useQuery } from '@apollo/client';
import type { OperationVariables } from '@apollo/client/core';
import type { QueryHookOptions } from '@apollo/client/react/types/types';
import { getOperationName } from '@apollo/client/utilities';
import type { ApolloQueryContext } from '@aurora/shared-types/apollo/tracing';
import ParentFrameSupplier from '@aurora/shared-utils/analytics/ParentFrameSupplier';
import { filterNullInClauseVariables } from '@aurora/shared-utils/helpers/objects/ObjectHelper';
import type { DocumentNode } from 'graphql';
import { useContext } from 'react';
import SwitchUserContext from './context/SwitchUserContext/SwitchUserContext';
import useTracing from './useTracing';

export type ExtendedQueryResult<TData, TVariables> = QueryResult<TData, TVariables> & {
  /**
   * Ready signifies if data from the query is available.
   */
  ready: boolean;
};

/**
 * Wrapper around `useQuery` that adds tracking info to the `context` property.
 *
 * @param module the module of the component
 * @param query the query
 * @param options the query options
 * @param bypassSwitchUser whether or not to bypass switched user
 *
 * @author Adam Ayres
 */
export default function useQueryWithTracing<TData = unknown, TVariables = OperationVariables>(
  module: NodeModule | string,
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: QueryHookOptions<TData, TVariables>,
  bypassSwitchUser?: boolean
): ExtendedQueryResult<TData, TVariables> {
  const operationName: string = getOperationName(query);
  const tracingContext: ApolloQueryContext = useTracing<
    TVariables,
    QueryHookOptions<TData, TVariables>
  >(module, operationName, options);
  const { switchUserId } = useContext(SwitchUserContext);
  const parentFrameId = ParentFrameSupplier.getParentFrameId();

  const context: ApolloQueryContext = {
    ...tracingContext,
    parentFrameId,
    switchUserId: bypassSwitchUser ? undefined : switchUserId
  };
  if (options?.variables) {
    filterNullInClauseVariables(options.variables as Record<string, unknown>);
  }

  const finalOptions: QueryHookOptions<TData, TVariables> = {
    ...options,
    context
  };

  const queryResult = useQuery<TData, TVariables>(query, finalOptions);
  const { data, loading, error } = queryResult;

  return { ...queryResult, loading, ready: !error && !loading && data != null };
}
